import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { eur } from "../components/common";
import { ShieldCheck, Check, X } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const PERIODO_LABEL = { anual: "Anual", "3anos": "3 anos", "5anos": "5 anos" };
const PERIODO_ORDER = ["anual", "3anos", "5anos"];

function planPriceFor(plan, periodo) {
  // Preco mensal do plano e a fonte de verdade
  const mensalMap = { anual: plan.preco_mensal_1ano, "3anos": plan.preco_mensal_3anos, "5anos": plan.preco_mensal_5anos };
  return mensalMap[periodo] || 0;
}
function planTotalFor(plan, periodo) {
  const totalMap = { anual: plan.preco_anual, "3anos": plan.preco_3anos, "5anos": plan.preco_5anos };
  return totalMap[periodo] || 0;
}
function planPeriodos(plan) {
  const disponiveis = (plan.periodos_disponiveis && plan.periodos_disponiveis.length > 0)
    ? plan.periodos_disponiveis.filter((p) => p !== "mensal")
    : [];
  const list = PERIODO_ORDER.filter((p) => disponiveis.includes(p));
  return list.length ? list : ["anual"];
}

function Footer() {
  return (
    <footer className="bg-navy text-white/70 py-12 px-8 mt-20">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="text-blue" size={24} />
            <span className="text-white font-bold text-lg">Cogniseg</span>
          </div>
          <p className="text-light-blue text-xs tracking-[0.2em] uppercase" style={{ color: "#5BB8E8" }}>Security and Compliance</p>
        </div>
        <div>
          <div className="text-white font-semibold mb-2">Contactos</div>
          <p>www.cogniseg.com</p>
          <p>info@cogniseg.com</p>
          <p>+351 253 00 89 89</p>
          <p>+351 211 45 23 34</p>
        </div>
        <div>
          <div className="text-white font-semibold mb-2">Morada</div>
          <p>Av. D. Joao II, Lote 1.07.2.1, Piso 0 - 1990-096 Lisboa</p>
        </div>
      </div>
    </footer>
  );
}

export default function Store() {
  const [catalog, setCatalog] = useState([]);
  const [checkout, setCheckout] = useState(null);

  useEffect(() => { api.get("/public/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-tint">
        <div className="max-w-6xl mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-blue" size={26} />
            <span className="text-navy font-bold text-xl tracking-tight">Cogniseg</span>
          </div>
          <span className="text-xs uppercase tracking-[0.2em] text-muted2" style={{ color: "#5BB8E8" }}>Security &amp; Compliance</span>
        </div>
      </header>

      <section className="relative">
        <img src="https://images.pexels.com/photos/9300768/pexels-photo-9300768.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0D1B3E]/85" />
        <div className="relative max-w-6xl mx-auto px-8 py-28">
          <div className="text-light-blue uppercase text-xs font-semibold tracking-[0.2em] mb-4" style={{ color: "#5BB8E8" }}>Portfolio SaaS</div>
          <h1 className="text-white text-4xl md:text-6xl font-bold tracking-tight leading-none max-w-3xl">
            Plataformas de seguranca e conformidade, prontas a usar.
          </h1>
          <p className="text-white/70 mt-6 max-w-xl leading-relaxed">
            Escolha um plano, subscreva online e tenha a sua instancia provisionada automaticamente. Faturacao e conformidade fiscal PT incluidas.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-8 py-16">
        <h2 className="text-navy text-3xl font-bold tracking-tight mb-10">As nossas plataformas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {catalog.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="border border-tint rounded-xl p-6 bg-white hover:shadow-lg transition-shadow flex flex-col"
              data-testid={`store-platform-${p.slug}`}
            >
              <h3 className="text-navy font-bold text-xl tracking-tight">{p.nome}</h3>
              <p className="text-muted2 text-sm mt-2 mb-5 leading-relaxed flex-1">{p.descricao}</p>
              <div className="space-y-4">
                {(p.plans || []).map((plan) => {
                  const periodos = planPeriodos(plan);
                  return (
                    <div key={plan.id} className="border border-tint rounded-lg p-3" data-testid={`store-plan-${plan.sku}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-navy">{plan.nome}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {periodos.map((per) => {
                          const mensal = planPriceFor(plan, per);
                          const total = planTotalFor(plan, per);
                          if (!mensal && !total) return null;
                          // desconto face ao mensal 1 ano
                          const baseMensal = planPriceFor(plan, "anual");
                          const off = baseMensal > 0 && per !== "anual" ? Math.round((1 - mensal / baseMensal) * 100) : 0;
                          return (
                            <button
                              key={per}
                              onClick={() => setCheckout({ platform: p, plan, periodo: per })}
                              data-testid={`store-plan-${plan.sku}-${per}`}
                              className="flex flex-col items-start px-3 py-2 rounded-lg bg-tint hover:bg-blue hover:text-white text-navy transition-colors text-left"
                            >
                              <span className="text-[10px] uppercase tracking-wide font-semibold opacity-80">{PERIODO_LABEL[per]}</span>
                              <span className="font-bold">{eur(mensal)}/mes</span>
                              <span className="text-[10px] opacity-80">{eur(total)} total{off > 0 ? ` (${off}% off)` : ""}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {checkout && <Checkout {...checkout} onClose={() => setCheckout(null)} />}
      <Footer />
    </div>
  );
}

function Checkout({ platform, plan, periodo, onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ org: "", nif: "", email: "", telefone: "", dpa: false, recorrente: false });
  const [done, setDone] = useState(null);
  const [mbRef, setMbRef] = useState(null);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm({ ...form, [k]: v });

  const preco = planTotalFor(plan, periodo);
  const mensal = planPriceFor(plan, periodo);
  const itens = [{ plano_id: plan.id, nome: `${platform.nome} ${plan.nome} (${PERIODO_LABEL[periodo]})`, periodo, quantidade: 1, preco }];

  const payMock = async () => {
    if (!form.org || !form.nif || !form.dpa) return toast.error("Preencha os dados e aceite o DPA");
    setBusy(true);
    try {
      await api.post("/public/orders", {
        origem: "online", organizacao_nome: form.org, nif: form.nif, itens,
        desconto_percent: 0, desconto_origem: "tabela", validacao_tecnica_necessaria: false,
      });
      setDone({ gateway: "stripe" }); setStep(3);
      toast.success("Pagamento confirmado e instancia provisionada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const payEuPago = async (metodo) => {
    if (!form.org || !form.nif || !form.dpa) return toast.error("Preencha os dados e aceite o DPA");
    if (metodo === "mbway" && !form.telefone) return toast.error("Indique o telemovel para MB WAY");
    setBusy(true);
    try {
      const { data } = await api.post("/public/eupago/checkout", {
        organizacao_nome: form.org, nif: form.nif, email: form.email, telefone: form.telefone, metodo, itens,
        recorrente: metodo === "card" ? !!form.recorrente : false,
        periodicidade: PERIODO_LABEL[periodo] || "Mensal",
      });
      if (metodo === "card" && data.redirectUrl) { window.location.assign(data.redirectUrl); return; }
      if (metodo === "multibanco") {
        setMbRef({ entidade: data.entidade, referencia: data.referencia, valor: data.valor });
        setStep(4);
        toast.success("Referencia Multibanco gerada");
      } else {
        setDone({ gateway: "MB WAY", pendente: true }); setStep(3);
        toast.success("Pedido MB WAY enviado. Aprove na app.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="checkout-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="text-blue uppercase text-xs font-semibold tracking-[0.2em]">Checkout</div>
            <h2 className="text-navy text-xl font-bold tracking-tight">{platform.nome} - {PERIODO_LABEL[periodo]}</h2>
          </div>
          <button onClick={onClose} data-testid="checkout-close"><X className="text-muted2" /></button>
        </div>

        {step === 1 && (
          <>
            <div className="bg-tint rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-navy font-semibold text-sm">Total ({PERIODO_LABEL[periodo]})</span>
                <span className="text-navy font-bold text-lg">{eur(preco)} <span className="text-xs text-muted2">+ IVA</span></span>
              </div>
              <div className="text-xs text-muted2 text-right mt-0.5">{eur(mensal)}/mes</div>
            </div>
            <label className="block text-sm font-semibold text-navy mb-1">Empresa</label>
            <input value={form.org} onChange={(e) => upd("org", e.target.value)} data-testid="checkout-org" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
            <label className="block text-sm font-semibold text-navy mb-1">NIF</label>
            <input value={form.nif} onChange={(e) => upd("nif", e.target.value)} data-testid="checkout-nif" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
            <label className="block text-sm font-semibold text-navy mb-1">Email</label>
            <input value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="checkout-email" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
            <label className="block text-sm font-semibold text-navy mb-1">Telemovel (MB WAY)</label>
            <input value={form.telefone} onChange={(e) => upd("telefone", e.target.value)} data-testid="checkout-phone" placeholder="912345678" className="w-full mb-4 px-3 py-2 border border-tint rounded-lg" />
            <label className="flex items-start gap-2 mb-6 text-sm text-muted2 cursor-pointer">
              <input type="checkbox" checked={form.dpa} onChange={(e) => upd("dpa", e.target.checked)} data-testid="checkout-dpa" className="mt-1" />
              Aceito os termos e o acordo de tratamento de dados (DPA), com dados alojados na UE.
            </label>
            <button onClick={() => setStep(2)} data-testid="checkout-continue" className="w-full bg-blue text-white py-2.5 rounded-lg font-semibold hover:bg-[#5BB8E8] transition-colors">
              Continuar para pagamento
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-muted2 text-sm mb-4">Escolha o metodo de pagamento:</p>
            <div className="space-y-3">
              <button disabled={busy} onClick={() => payEuPago("multibanco")} data-testid="pay-multibanco" className="w-full px-4 py-3 rounded-lg border border-tint hover:bg-tint text-navy font-semibold transition-colors text-left disabled:opacity-50">
                Multibanco (Entidade / Referencia)
              </button>
              <button disabled={busy} onClick={() => payEuPago("mbway")} data-testid="pay-mbway" className="w-full px-4 py-3 rounded-lg border border-tint hover:bg-tint text-navy font-semibold transition-colors text-left disabled:opacity-50">
                MB WAY
              </button>
              <button disabled={busy} onClick={() => payEuPago("card")} data-testid="pay-card" className="w-full px-4 py-3 rounded-lg border border-tint hover:bg-tint text-navy font-semibold transition-colors text-left disabled:opacity-50">
                Cartao de credito (EuPago)
              </button>
            </div>
            <label className="flex items-start gap-2 mt-4 text-sm text-muted2 cursor-pointer">
              <input type="checkbox" checked={form.recorrente} onChange={(e) => upd("recorrente", e.target.checked)} data-testid="checkout-recorrente" className="mt-1" />
              Ativar renovacao automatica ({PERIODO_LABEL[periodo]}) - so cartao de credito
            </label>
            <button onClick={() => setStep(1)} className="mt-4 text-sm text-muted2 hover:text-blue">Voltar</button>
          </>
        )}

        {step === 3 && done && (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-600" size={32} />
            </div>
            <h3 className="text-navy font-bold text-xl mb-2">{done.pendente ? "Pagamento em curso" : "Subscricao ativa"}</h3>
            <p className="text-muted2 text-sm mb-6">
              {done.pendente
                ? `Pedido ${done.gateway} enviado. Apos a confirmacao do pagamento, a instancia e provisionada automaticamente e recebera um email.`
                : `Pagamento via ${done.gateway} confirmado. A sua instancia foi provisionada e a fatura emitida no Moloni.`}
            </p>
            <button onClick={onClose} data-testid="checkout-finish" className="w-full bg-blue text-white py-2.5 rounded-lg font-semibold hover:bg-[#5BB8E8] transition-colors">
              Concluir
            </button>
          </div>
        )}

        {step === 4 && mbRef && (
          <div className="py-4" data-testid="checkout-mb-ref">
            <h3 className="text-navy font-bold text-lg mb-3 text-center">Referencia Multibanco</h3>
            <div className="bg-tint rounded-lg p-4 space-y-2 text-center">
              <div><div className="text-xs text-muted2 uppercase tracking-wide">Entidade</div><div className="text-navy font-bold text-2xl tracking-widest">{mbRef.entidade}</div></div>
              <div><div className="text-xs text-muted2 uppercase tracking-wide">Referencia</div><div className="text-navy font-bold text-2xl tracking-widest">{mbRef.referencia}</div></div>
              <div><div className="text-xs text-muted2 uppercase tracking-wide">Valor</div><div className="text-navy font-bold text-xl">{eur(mbRef.valor)}</div></div>
            </div>
            <p className="text-muted2 text-xs mt-4 text-center">Efetue o pagamento no seu homebanking ou caixa Multibanco. Apos a confirmacao, a instancia e provisionada automaticamente.</p>
            <button onClick={onClose} data-testid="checkout-finish" className="w-full mt-5 bg-blue text-white py-2.5 rounded-lg font-semibold hover:bg-[#5BB8E8] transition-colors">
              Concluir
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
